(() => {
  'use strict';

  const MAX_UPLOAD_BYTES = 15 * 1024 * 1024;
  const UPLOAD_MAX_EDGE = 1024;
  const UPLOAD_JPEG_QUALITY = 0.84;
  const UPLOAD_PASSTHROUGH_BYTES = 1.5 * 1024 * 1024;
  const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
  const POLL_INTERVAL_MS = 1800;
  const MAX_POLLS = 120;
  const SHADE_COLORS = {
    'champagne-blonde': '#c6a98d',
    'honey-blonde': '#ad895d',
    'dark-blonde': '#80694f',
    'light-brown': '#71513c',
    'medium-brown': '#4d3327',
    'dark-brown': '#2e211c',
    'gray': '#8b8987',
    'black': '#171515',
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-evt-root]').forEach(initTryOn);

  function initTryOn(root) {
    if (root.dataset.evtInitialized === 'true') return;
    root.dataset.evtInitialized = 'true';

    const q = (selector) => root.querySelector(selector);
    const qa = (selector) => Array.from(root.querySelectorAll(selector));
    const modal = q('[data-evt-modal]');
    if (!modal) return;

    const panel = q('.evt-modal__panel');
    const fileInput = q('[data-evt-file]');
    const dropzone = q('[data-evt-dropzone]');
    const endpoint = (root.dataset.evtEndpoint || '').trim().replace(/\/+$/, '');
    const state = {
      screen: 'upload', file: null, uploadFilePromise: null, previewUrl: '', record: null,
      style: 'natural-blend', styleTimer: null, lastFocus: null, controller: null, busy: false,
    };

    function apiUrl(path) {
      if (!endpoint) throw new Error('Add the try-on backend URL in this section’s theme settings.');
      return `${endpoint}${path}`;
    }

    async function readJson(response) {
      const body = await response.json().catch(() => ({}));
      if (!response.ok) {
        const details = Array.isArray(body.details) ? body.details.map((item) => item.message).filter(Boolean).join(' ') : '';
        throw new Error(body.message || body.error || details || `The request failed (${response.status}).`);
      }
      return body.data || body;
    }

    async function request(path, options = {}) {
      try {
        const response = await fetch(apiUrl(path), { ...options, headers: { Accept: 'application/json', ...(options.headers || {}) } });
        return await readJson(response);
      } catch (error) {
        if (error?.name === 'AbortError') throw error;
        if (error instanceof TypeError) throw new Error('The try-on service could not be reached. Check the backend URL, HTTPS, and PUBLIC_APP_ORIGIN settings.');
        throw error;
      }
    }

    function showNotice(message) {
      const notice = q('[data-evt-notice]');
      notice.textContent = message;
      notice.hidden = false;
      notice.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    function clearNotice() {
      const notice = q('[data-evt-notice]');
      notice.textContent = '';
      notice.hidden = true;
    }

    function showScreen(name) {
      qa('[data-evt-screen]').forEach((screen) => {
        const active = screen.dataset.evtScreen === name;
        screen.hidden = !active;
        screen.classList.toggle('is-active', active);
      });
      state.screen = name;
      clearNotice();
      q('[data-evt-back]').hidden = name !== 'style';
      const activeIndex = name === 'upload' ? 0 : name === 'style' ? 1 : 2;
      qa('[data-evt-progress-step]').forEach((step, index) => {
        step.classList.toggle('is-active', index === activeIndex);
        step.classList.toggle('is-done', index < activeIndex);
      });
      panel.scrollTop = 0;
    }

    function openModal() {
      state.lastFocus = document.activeElement;
      modal.hidden = false;
      document.body.classList.add('evt-lock');
      requestAnimationFrame(() => {
        modal.classList.add('is-open');
        q('[data-evt-close]:not(.evt-modal__backdrop)')?.focus();
      });
    }

    function closeModal() {
      window.clearTimeout(state.styleTimer);
      state.styleTimer = null;
      modal.classList.remove('is-open');
      document.body.classList.remove('evt-lock');
      window.setTimeout(() => { modal.hidden = true; }, 220);
      state.lastFocus?.focus?.();
    }

    function validateFile(file) {
      if (!file) return 'Choose a photo first.';
      if (!ACCEPTED_TYPES.has(file.type)) return 'Please choose a JPG, PNG, or WEBP image.';
      if (file.size > MAX_UPLOAD_BYTES) return 'Please choose an image smaller than 15 MB.';
      return '';
    }

    function loadImage(file) {
      return new Promise((resolve, reject) => {
        const url = URL.createObjectURL(file);
        const image = new Image();
        image.onload = () => { URL.revokeObjectURL(url); resolve(image); };
        image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('The photo could not be prepared for upload.')); };
        image.src = url;
      });
    }

    async function optimizeUpload(file) {
      const image = await loadImage(file);
      const longestEdge = Math.max(image.naturalWidth, image.naturalHeight);
      if (longestEdge <= UPLOAD_MAX_EDGE && file.size <= UPLOAD_PASSTHROUGH_BYTES && file.type === 'image/jpeg') return file;

      const scale = Math.min(1, UPLOAD_MAX_EDGE / longestEdge);
      const canvas = document.createElement('canvas');
      canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
      canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
      const context = canvas.getContext('2d', { alpha: false });
      if (!context) return file;
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);

      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', UPLOAD_JPEG_QUALITY));
      if (!blob || blob.size >= file.size) return file;
      const baseName = file.name.replace(/\.[^.]+$/, '') || 'everelle-photo';
      return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: file.lastModified });
    }

    function acceptFile(file) {
      const error = validateFile(file);
      if (error) { showNotice(error); return; }
      clearNotice();
      state.file = file;
      state.uploadFilePromise = optimizeUpload(file).catch(() => file);
      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
      state.previewUrl = URL.createObjectURL(file);
      q('[data-evt-preview-image]').src = state.previewUrl;
      q('[data-evt-processing-image]').src = state.previewUrl;
      q('[data-evt-upload-empty]').hidden = true;
      q('[data-evt-upload-preview]').hidden = false;
      q('[data-evt-photo-next]').disabled = false;
      window.setTimeout(() => showScreen('style'), 120);
    }

    function selectedStyle() {
      return q('input[name^="evt-style-"]:checked')?.value || 'natural-blend';
    }

    function setBusy(busy) {
      state.busy = busy;
      modal.classList.toggle('is-busy', busy);
    }

    function sleep(ms, signal) {
      return new Promise((resolve, reject) => {
        const timer = window.setTimeout(resolve, ms);
        signal?.addEventListener('abort', () => {
          window.clearTimeout(timer);
          reject(new DOMException('Aborted', 'AbortError'));
        }, { once: true });
      });
    }

    function updateProcessing(record) {
      const title = q('[data-evt-processing-title]');
      const copy = q('[data-evt-processing-copy]');
      if (record.status === 'queued') {
        const ahead = Number(record.queue?.ahead ?? 0);
        const position = Number(record.queue?.position ?? ahead + 1);
        title.textContent = ahead === 0 ? 'Your preview is next…' : `You’re #${position} in line`;
        copy.textContent = ahead === 0
          ? 'Your preview will start automatically in a moment.'
          : `${ahead} ${ahead === 1 ? 'preview is' : 'previews are'} ahead of you. Keep this window open—we’ll update your place automatically.`;
      } else if (record.status === 'rendering') {
        title.textContent = 'Blending your Everelle topper…';
        copy.textContent = 'Your shade is matched. Now we’re creating realistic fullness and your chosen hairstyle.';
      } else {
        title.textContent = 'Finding your closest shade…';
        copy.textContent = 'We’re reading your root depth and undertone, then selecting the closest Everelle shade.';
      }
    }

    async function poll(initialRecord, signal) {
      let record = initialRecord;
      for (let attempt = 0; attempt < MAX_POLLS; attempt += 1) {
        updateProcessing(record);
        if (record.status === 'completed') { renderResult(record); return; }
        if (record.status === 'needs_photo') {
          showScreen('upload');
          showNotice(record.message || record.issues?.[0] || 'Choose a clearer photo with your face and front parting visible.');
          return;
        }
        if (record.status === 'failed') throw new Error(record.message || 'We could not create this preview. Please try again.');
        await sleep(POLL_INTERVAL_MS, signal);
        record = await request(`/try-ons/${encodeURIComponent(record.id)}`, { signal });
      }
      throw new Error('The preview is taking longer than expected. Please try again.');
    }

    async function createPreview() {
      state.styleTimer = null;
      const error = validateFile(state.file);
      if (error) { showScreen('upload'); showNotice(error); return; }
      state.style = selectedStyle();
      state.controller?.abort();
      state.controller = new AbortController();
      const form = new FormData();
      showScreen('processing');
      setBusy(true);
      try {
        const uploadFile = await (state.uploadFilePromise || Promise.resolve(state.file));
        form.append('image', uploadFile, uploadFile.name);
        form.append('style', state.style);
        const record = await request('/try-ons', { method: 'POST', body: form, signal: state.controller.signal });
        await poll(record, state.controller.signal);
      } catch (error) {
        if (error?.name !== 'AbortError') { showScreen('style'); showNotice(error.message || 'We could not create your preview.'); }
      } finally {
        setBusy(false);
      }
    }

    function renderResult(record) {
      state.record = record;
      state.style = record.result.style.key;
      root.dataset.evTryonCompleted = 'true';
      root.dataset.evTryonJobId = record.id || '';
      root.dataset.evTryonShade = record.result.shade.key || '';
      root.dataset.evTryonStyle = record.result.style.key || '';
      root.dataset.evTryonSource = 'preview-completed';
      q('[data-evt-result-image]').src = record.result.imageUrl;
      q('[data-evt-match-name]').textContent = record.result.shade.name;
      const shadeButton = q(`[data-ev-shade][data-shade-key="${CSS.escape(record.result.shade.key)}"]`);
      const matchSwatch = q('[data-evt-match-swatch]');
      const matchTexture = q('[data-evt-match-texture]');
      const textureUrl = shadeButton?.dataset.texture || '';
      matchSwatch.style.background = SHADE_COLORS[record.result.shade.key] || '#6b625d';
      matchTexture.hidden = !textureUrl;
      if (textureUrl) {
        matchTexture.src = textureUrl;
        matchTexture.alt = `${record.result.shade.name} hair swatch`;
      } else {
        matchTexture.removeAttribute('src');
        matchTexture.alt = '';
      }
      q('[data-evt-match-mode]').textContent = record.shadeSelection === 'manual' ? 'Selected by you' : 'Auto-matched';
      showScreen('result');
    }

    function checkoutMatchedShade() {
      const key = state.record?.result?.shade?.key;
      const shadeButton = key ? q(`[data-ev-shade][data-shade-key="${CSS.escape(key)}"]`) : null;
      if (!shadeButton?.dataset.variant) { showNotice('This matched shade is not currently available for checkout.'); return; }
      shadeButton.click();
      const checkoutButton = q('[data-ev-atc]');
      const purchaseForm = checkoutButton?.form;
      if (!purchaseForm || !checkoutButton) { showNotice('Checkout is unavailable for this shade.'); return; }
      root.dataset.evTryonSource = 'result-cta';
      if (checkoutButton.disabled) {
        closeModal();
        q('[data-ev-bundle-shades]')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      purchaseForm.requestSubmit(checkoutButton);
    }

    function keepFocusInside(event) {
      if (event.key === 'Escape') { closeModal(); return; }
      if (event.key !== 'Tab') return;
      const focusable = qa('button:not([disabled]):not([hidden]), [href], input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])').filter((item) => item.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    }

    qa('[data-evt-open]').forEach((button) => button.addEventListener('click', openModal));
    qa('[data-evt-close]').forEach((button) => button.addEventListener('click', closeModal));
    q('[data-evt-back]').addEventListener('click', () => showScreen('upload'));
    modal.addEventListener('keydown', keepFocusInside);
    fileInput.addEventListener('change', () => acceptFile(fileInput.files?.[0]));
    ['dragenter', 'dragover'].forEach((type) => dropzone.addEventListener(type, (event) => {
      event.preventDefault(); dropzone.classList.add('is-dragging');
    }));
    ['dragleave', 'drop'].forEach((type) => dropzone.addEventListener(type, (event) => {
      event.preventDefault(); dropzone.classList.remove('is-dragging');
    }));
    dropzone.addEventListener('drop', (event) => acceptFile(event.dataTransfer?.files?.[0]));
    q('[data-evt-photo-next]').addEventListener('click', () => showScreen('style'));
    qa('input[name^="evt-style-"]').forEach((input) => input.addEventListener('change', () => {
      qa('.evt-style').forEach((label) => label.classList.toggle('is-selected', label.contains(input)));
      state.style = input.value;
      window.clearTimeout(state.styleTimer);
      state.styleTimer = window.setTimeout(createPreview, 140);
    }));
    q('[data-evt-checkout]').addEventListener('click', checkoutMatchedShade);
  }

  initAll();
  if (!window.__everelleTryOnSectionListener) {
    window.__everelleTryOnSectionListener = true;
    document.addEventListener('shopify:section:load', (event) => initAll(event.target));
  }
})();
